import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import LinkButton from '@atlaskit/button/link';
import DatabaseIcon from '@atlaskit/icon/core/database';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import LinkIcon from '@atlaskit/icon/core/link';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import SmartLinkIcon from '@atlaskit/icon/core/smart-link';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import AiGenerativeSlidesIcon from '@atlaskit/icon-lab/core/ai-generative-slides';
import { Text } from '@atlaskit/primitives/compiled/text';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import {
	DATABASE_VIEW,
	EMBED_VIEW,
	FOLDER_VIEW,
	SLIDES_VIEW,
	VIEW_BLOG,
	VIEW_PAGE,
	WHITEBOARD_BOARD,
} from '@confluence/named-routes/entry-points/routes.ts';
import { generateSlug } from '@confluence/route-manager-utils/entry-points/url.ts';

import type { TitleReferences } from './__types__/SpaceSuggestion.ts';
import { parseSuggestionTitle, type TitleSegment } from './utils/parseSuggestionTitle.ts';

type LinkSegment = Extract<TitleSegment, { type: 'link' }>;

// All `@atlaskit/icon` (core) and `@atlaskit/icon-lab` (core) default exports
// share the same prop signature, so the page icon's type stands in for any of them.
type ContentTypeIcon = typeof PageIcon;

/**
 * Maps a content type to its icon. `contentType` only affects the icon — an
 * absent or unrecognised type renders no icon (`undefined`).
 */
function iconForContentType(contentType?: string): ContentTypeIcon | undefined {
	switch (contentType) {
		case 'page':
			return PageIcon;
		case 'blogpost':
			return QuotationMarkIcon;
		case 'database':
			return DatabaseIcon;
		case 'whiteboard':
			return WhiteboardIcon;
		case 'slide':
			return AiGenerativeSlidesIcon;
		case 'embed':
			return SmartLinkIcon;
		case 'url':
			return LinkIcon;
		case 'folder':
			return FolderClosedIcon;
		default:
			return undefined;
	}
}

/**
 * Maps a content type to the named route for its view page. Each of these
 * routes shares the same `spaceKey`/`contentId`/`contentSlug?` param shape, so
 * a route neither declaring nor needing `contentSlug` simply ignores it.
 * An absent or unrecognised type falls back to `VIEW_PAGE`, matching the
 * behaviour for the overwhelmingly common case (a page reference).
 */
function routeForContentType(contentType?: string): typeof VIEW_PAGE {
	switch (contentType) {
		case 'blogpost':
			return VIEW_BLOG;
		case 'database':
			return DATABASE_VIEW;
		case 'whiteboard':
			return WHITEBOARD_BOARD;
		case 'slide':
			return SLIDES_VIEW;
		case 'embed':
			return EMBED_VIEW;
		case 'folder':
			return FOLDER_VIEW;
		case 'page':
		default:
			return VIEW_PAGE;
	}
}

/**
 * Builds the content link href. Falls back to the bare `/wiki/spaces/_/pages/{contentId}`
 * form when `spaceKey` is unavailable, since every route above requires it.
 */
function buildContentHref(link: LinkSegment, spaceKey?: string): string {
	if (!spaceKey) {
		return `/wiki/spaces/_/pages/${link.contentId}`;
	}
	return routeForContentType(link.contentType).toUrl({
		spaceKey,
		contentId: link.contentId,
		contentSlug: generateSlug(link.title),
	});
}

function ContentLinkButton({ link, spaceKey }: { link: LinkSegment; spaceKey?: string }) {
	const { formatMessage } = useIntl();

	return (
		<LinkButton
			appearance="default"
			spacing="compact"
			href={buildContentHref(link, spaceKey)}
			target="_blank"
			rel="noopener noreferrer"
			iconBefore={iconForContentType(link.contentType)}
		>
			{link.title}
			<VisuallyHidden>{formatMessage(i18n.opensInNewTab)}</VisuallyHidden>
		</LinkButton>
	);
}

/**
 * Renders a suggestion title, replacing each embedded content-link object with
 * a `LinkButton` (icon + new-tab link) while preserving the surrounding text.
 * Titles with no parseable objects render as plain text, unchanged.
 */
export function SuggestionSummary({
	text,
	titleReferences,
	spaceKey,
}: {
	text: string;
	titleReferences?: TitleReferences;
	/** The space this suggestion belongs to, used to build a fully-qualified content link. */
	spaceKey?: string;
}): React.JSX.Element {
	const segments = parseSuggestionTitle(text, titleReferences);

	return (
		<Text as="span">
			{segments.map((segment, index) =>
				segment.type === 'text' ? (
					// eslint-disable-next-line react/no-array-index-key -- segments are positional and the title is static per render
					<React.Fragment key={index}>{segment.value}</React.Fragment>
				) : (
					// eslint-disable-next-line react/no-array-index-key -- see above
					<ContentLinkButton key={index} link={segment} spaceKey={spaceKey} />
				),
			)}
		</Text>
	);
}

const i18n = defineMessages({
	opensInNewTab: {
		id: 'space-suggestions.suggestion-summary.opens-in-new-tab',
		defaultMessage: '(opens in a new tab)',
		description:
			'Visually hidden hint appended to a content link in a suggestion title, telling screen reader users the link opens in a new browser tab',
	},
});
